/**
 * FeeConfigurationEditor.tsx
 * Right-pane editor for broker fee configurations
 *
 * Renders a Card with:
 * - Breadcrumb navigation showing the selected node's hierarchy
 * - Notes popover for independent notes editing (edit mode only)
 * - "Inherit from Parent" action (edit mode, when ancestor exists)
 * - Commission section (Financing & FX configured at broker level)
 * - Mode switching: edit mode (direct config) vs inherited mode (read-only + banner)
 * - Footer with Save/Discard (edit mode only)
 *
 * Builds the fee tree internally to resolve selectedNodeId into a FeeTreeNode,
 * then looks up the active BrokerFeeConfiguration for that node.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { NotepadText, Save, Info, AlertTriangle, Globe } from 'lucide-react';

// Workspace packages
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Breadcrumbs,
  BreadcrumbItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  TextArea,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  useToastNotification,
} from '@enfomentis/ui';

import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';

import { buildFeeTree } from '../feeTree/buildFeeTree';
import {
  findNodeById,
  findInheritedConfig,
  buildNodeIdFromConfig,
  buildConfigName,
  buildFeeConfigPath,
} from '../feeTree/feeConfigUtils';
import type { CommissionStructure } from '@/types/shared/feeStructures/CommissionStructure';
import type { CurrencyConversionStructure } from '@/types/shared/feeStructures/CurrencyConversionStructure';
import type { OvernightFinancingStructure } from '@/types/shared/feeStructures/OvernightFinancingStructure';

import { useBrokers } from '../context/BrokersContext';
import type { EditorMode } from './feeEditorTypes';
import { CommissionTabContent } from './CommissionTabContent';

// =============================================================================
// Types
// =============================================================================

interface FeeConfigurationEditorProps {
  /** Currently selected tree node ID */
  selectedNodeId: string;
  /** Fee configurations from the selected broker */
  feeConfigurations: BrokerFeeConfiguration[];
  /** Callback to navigate to a different tree node (breadcrumb clicks, stale selection clear) */
  onNodeSelect: (nodeId: string | null) => void;
  /** Ref kept current with editor's per-tab dirty state */
  isDirtyRef: React.MutableRefObject<boolean>;
  /** Ref populated with editor's discard-all callback for navigation modal */
  discardRef: React.MutableRefObject<(() => void) | null>;
  /** Ref populated with editor's save callback for save-then-navigate */
  saveRef: React.MutableRefObject<(() => Promise<boolean>) | null>;
}


// =============================================================================
// Component
// =============================================================================

export const FeeConfigurationEditor: React.FC<FeeConfigurationEditorProps> = ({
  selectedNodeId,
  feeConfigurations,
  onNodeSelect,
  isDirtyRef,
  discardRef,
  saveRef,
}) => {
  // ---------------------------------------------------------------------------
  // Context
  // ---------------------------------------------------------------------------

  const { selectedBroker, updateFeeConfiguration, addFeeConfiguration, removeFeeConfiguration } = useBrokers();
  const accountCurrency = selectedBroker?.feeMetadata?.defaultAccountCurrency?.trim() || null;
  const { showToast } = useToastNotification();

  // ---------------------------------------------------------------------------
  // Fee Tree & Node Lookup
  // ---------------------------------------------------------------------------

  const displayMaps = useMemo(
    () => ({
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    }),
    []
  );

  const tree = useMemo(
    () => buildFeeTree(feeConfigurations, displayMaps),
    [feeConfigurations, displayMaps]
  );

  const selectedNode = useMemo(
    () => findNodeById(tree, selectedNodeId),
    [tree, selectedNodeId]
  );


  // ---------------------------------------------------------------------------
  // Active Fee Configuration & Mode
  // ---------------------------------------------------------------------------

  const activeFeeConfig = useMemo((): BrokerFeeConfiguration | null => {
    if (!selectedNode || selectedNode.feeConfigIds.length === 0) {
      return null;
    }
    const activeConfig = feeConfigurations.find(
      (c) =>
        selectedNode.feeConfigIds.includes(c.id) && c.status === 'active'
    );
    return activeConfig ?? null;
  }, [selectedNode, feeConfigurations]);

  const mode: EditorMode = activeFeeConfig ? 'edit' : 'inherited';

  // ---------------------------------------------------------------------------
  // Nearest Ancestor Config (computed regardless of mode)
  // ---------------------------------------------------------------------------

  const nearestAncestorConfig = useMemo((): BrokerFeeConfiguration | null => {
    if (!selectedNode) return null;
    return findInheritedConfig(selectedNode, feeConfigurations);
  }, [selectedNode, feeConfigurations]);

  const nearestAncestorNodeId = useMemo((): string | null => {
    if (!nearestAncestorConfig) return null;
    return buildNodeIdFromConfig(nearestAncestorConfig);
  }, [nearestAncestorConfig]);

  const nearestAncestorLabel = useMemo((): string | null => {
    if (!nearestAncestorConfig) return null;
    return buildConfigName(
      nearestAncestorConfig.category,
      nearestAncestorConfig.instrumentId,
      displayMaps
    );
  }, [nearestAncestorConfig, displayMaps]);

  // ---------------------------------------------------------------------------
  // Dirty State Baseline Refs & Cross-Tab Conflict State
  // ---------------------------------------------------------------------------

  const baselineCommissionRef = useRef<string>('null');
  const baselineOvernightRef = useRef<string>('null');
  const baselineConversionRef = useRef<string>('null');

  // JSON snapshot refs for local state — read by sync effect guard
  const localCommissionJsonRef = useRef<string>('null');
  const localOvernightJsonRef = useRef<string>('null');
  const localConversionJsonRef = useRef<string>('null');

  // Cross-tab conflict state
  const [hasQueuedFeeUpdate, setHasQueuedFeeUpdate] = useState(false);
  const [dismissedFeeConflict, setDismissedFeeConflict] = useState(false);

  // Queued external update (blocked while dirty)
  const queuedConfigIdRef = useRef<string | null>(null);
  const queuedCommissionRef = useRef<CommissionStructure | null>(null);
  const queuedOvernightRef = useRef<OvernightFinancingStructure | null>(null);
  const queuedConversionRef = useRef<CurrencyConversionStructure | null>(null);

  // ---------------------------------------------------------------------------
  // Local Tab State (owned here for dirty tracking)
  // ---------------------------------------------------------------------------

  const [localCommission, setLocalCommission] = useState<CommissionStructure | null>(null);
  const [localOvernight, setLocalOvernight] = useState<OvernightFinancingStructure | null>(null);
  const [localConversion, setLocalConversion] = useState<CurrencyConversionStructure | null>(null);

  // Sync local tab state from active config when config ID or data changes.
  // Clears to null when activeFeeConfig is null (inherited mode / "Inherit from Parent").
  const activeConfigId = activeFeeConfig?.id ?? null;
  const activeFeesUpdatedAt = activeFeeConfig?.feesUpdatedAt ?? null;

  // Ref for activeConfigId — used by applyQueuedFeeUpdate to check staleness
  const activeConfigIdRef = useRef<string | null>(activeConfigId);
  activeConfigIdRef.current = activeConfigId;

  // Track previous config ID to detect node switch vs same-config update
  const prevConfigIdRef = useRef<string | null>(null);

  useEffect(() => {
    const isNodeSwitch = activeConfigId !== prevConfigIdRef.current;
    prevConfigIdRef.current = activeConfigId;

    // Check if any tab is dirty (via JSON snapshot refs synced in render body)
    const isAnyTabDirty =
      localCommissionJsonRef.current !== baselineCommissionRef.current ||
      localOvernightJsonRef.current !== baselineOvernightRef.current ||
      localConversionJsonRef.current !== baselineConversionRef.current;

    // If dirty AND same config (external update, not node switch) → queue, don't overwrite
    if (isAnyTabDirty && !isNodeSwitch && activeFeeConfig) {
      queuedConfigIdRef.current = activeConfigId;
      queuedCommissionRef.current = activeFeeConfig.commission;
      queuedOvernightRef.current = activeFeeConfig.overnightFinancing ?? null;
      queuedConversionRef.current = activeFeeConfig.currencyConversion ?? null;
      setHasQueuedFeeUpdate(true);
      setDismissedFeeConflict(false);
      return;
    }

    // Clean or new config → apply normally
    if (activeFeeConfig) {
      setLocalCommission(activeFeeConfig.commission);
      setLocalOvernight(activeFeeConfig.overnightFinancing ?? null);
      setLocalConversion(activeFeeConfig.currencyConversion ?? null);
      baselineCommissionRef.current = JSON.stringify(activeFeeConfig.commission);
      baselineOvernightRef.current = JSON.stringify(activeFeeConfig.overnightFinancing ?? null);
      baselineConversionRef.current = JSON.stringify(activeFeeConfig.currencyConversion ?? null);
    } else {
      setLocalCommission(null);
      setLocalOvernight(null);
      setLocalConversion(null);
      baselineCommissionRef.current = 'null';
      baselineOvernightRef.current = 'null';
      baselineConversionRef.current = 'null';
    }

    // Clear any queued state on clean apply
    queuedConfigIdRef.current = null;
    queuedCommissionRef.current = null;
    queuedOvernightRef.current = null;
    queuedConversionRef.current = null;
    setHasQueuedFeeUpdate(false);
    setDismissedFeeConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: uses primitive proxies (activeConfigId, activeFeesUpdatedAt) instead of activeFeeConfig object reference to avoid re-running when unrelated config fields change. JSON snapshot refs are read (not deps) for dirty guard.
  }, [activeConfigId, activeFeesUpdatedAt]);

  const handleCommissionChange = useCallback(
    (commission: CommissionStructure): void => {
      setLocalCommission(commission);
    },
    []
  );


  // ---------------------------------------------------------------------------
  // Save / Discard / Queued Update Handlers
  // ---------------------------------------------------------------------------

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!activeFeeConfig || !localCommission || isSaving) return false;

    // Dirty guard for programmatic callers (e.g. saveRef from BrokersTab nav modal).
    // Cannot use isDirtyForAnyTab here (TDZ — declared after this callback), so compute from refs.
    const anyDirty =
      localCommissionJsonRef.current !== baselineCommissionRef.current ||
      localOvernightJsonRef.current !== baselineOvernightRef.current ||
      localConversionJsonRef.current !== baselineConversionRef.current;
    if (!anyDirty) return false;

    setIsSaving(true);
    try {
      const success = await updateFeeConfiguration(activeFeeConfig.id, {
        commission: localCommission,
      });
      if (success) {
        // Update baseline to match saved state so sync effect sees clean state
        baselineCommissionRef.current = JSON.stringify(localCommission);
        showToast({ title: 'Fees saved', color: 'success' });
        setHasQueuedFeeUpdate(false);
      } else {
        showToast({ title: 'Failed to save fees. Please try again.', color: 'danger' });
      }
      return success;
    } finally {
      setIsSaving(false);
    }
  }, [activeFeeConfig, localCommission, isSaving, updateFeeConfiguration, showToast]);

  const handleDiscardCurrentTab = useCallback((): void => {
    if (!activeFeeConfig) return;
    setLocalCommission(activeFeeConfig.commission);
  }, [activeFeeConfig]);

  const applyQueuedFeeUpdate = useCallback((): void => {
    // Guard: only apply if queued data is for the current config
    if (queuedConfigIdRef.current !== activeConfigIdRef.current) {
      // Stale queue — clear without applying
      queuedConfigIdRef.current = null;
      queuedCommissionRef.current = null;
      queuedOvernightRef.current = null;
      queuedConversionRef.current = null;
      setHasQueuedFeeUpdate(false);
      setDismissedFeeConflict(false);
      return;
    }

    // Apply all queued state + update baselines
    setLocalCommission(queuedCommissionRef.current);
    setLocalOvernight(queuedOvernightRef.current);
    setLocalConversion(queuedConversionRef.current);
    baselineCommissionRef.current = JSON.stringify(queuedCommissionRef.current);
    baselineOvernightRef.current = JSON.stringify(queuedOvernightRef.current);
    baselineConversionRef.current = JSON.stringify(queuedConversionRef.current);

    queuedConfigIdRef.current = null;
    queuedCommissionRef.current = null;
    queuedOvernightRef.current = null;
    queuedConversionRef.current = null;
    setHasQueuedFeeUpdate(false);
    setDismissedFeeConflict(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Notes Popover State
  // ---------------------------------------------------------------------------

  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Sync notes text when config or popover opens
  useEffect(() => {
    if (isNotesOpen && activeFeeConfig) {
      setNotesText(activeFeeConfig.notes ?? '');
    }
  }, [isNotesOpen, activeFeeConfig]);

  const handleSaveNotes = useCallback(async (): Promise<void> => {
    if (!activeFeeConfig) return;

    // No-op if unchanged
    const currentNotes = activeFeeConfig.notes ?? '';
    if (notesText === currentNotes) {
      setIsNotesOpen(false);
      return;
    }

    setIsSavingNotes(true);
    try {
      const success = await updateFeeConfiguration(activeFeeConfig.id, { notes: notesText });
      if (success) {
        setIsNotesOpen(false);
      } else {
        showToast({ title: 'Failed to save notes', color: 'danger' });
      }
    } finally {
      setIsSavingNotes(false);
    }
  }, [activeFeeConfig, notesText, updateFeeConfiguration, showToast]);

  // ---------------------------------------------------------------------------
  // Customize Action (inherited mode → create new config)
  // ---------------------------------------------------------------------------

  const [isCustomizing, setIsCustomizing] = useState(false);

  const feeConfigPath = useMemo(() => {
    if (!selectedNode) return null;
    return buildFeeConfigPath(selectedNode);
  }, [selectedNode]);

  const handleCustomize = useCallback(async (): Promise<void> => {
    if (!feeConfigPath) return;

    setIsCustomizing(true);
    try {
      const result = await addFeeConfiguration(feeConfigPath);
      if (result.success) {
        onNodeSelect(result.nodeId);
        showToast({ title: 'Fee configuration created', color: 'success' });
      } else {
        showToast({ title: 'Failed to create fee configuration', color: 'danger' });
      }
    } finally {
      setIsCustomizing(false);
    }
  }, [feeConfigPath, addFeeConfiguration, onNodeSelect, showToast]);

  // ---------------------------------------------------------------------------
  // Inherit from Parent Action (edit mode → remove custom config)
  // ---------------------------------------------------------------------------

  const [isInheritConfirmOpen, setIsInheritConfirmOpen] = useState(false);
  const [isInheriting, setIsInheriting] = useState(false);

  const handleInheritConfirm = useCallback(async (): Promise<void> => {
    if (!activeFeeConfig || !selectedNode) return;

    setIsInheriting(true);
    try {
      const success = await removeFeeConfiguration(activeFeeConfig.id);
      if (success) {
        showToast({ title: 'Now inheriting fees from parent', color: 'success' });
        setIsInheritConfirmOpen(false);
        // Leaf nodes disappear from the tree after archiving — navigate to ancestor.
        // Mid-tier nodes stay (descendants keep them alive) and transition to inherited mode in-place.
        if (selectedNode.children.length === 0 && nearestAncestorNodeId) {
          onNodeSelect(nearestAncestorNodeId);
        }
      } else {
        showToast({ title: 'Failed to remove custom fees', color: 'danger' });
      }
    } finally {
      setIsInheriting(false);
    }
  }, [activeFeeConfig, removeFeeConfiguration, showToast, selectedNode, nearestAncestorNodeId, onNodeSelect]);

  // ---------------------------------------------------------------------------
  // Breadcrumb Items
  // ---------------------------------------------------------------------------

  const breadcrumbItems = useMemo((): Array<{
    label: string;
    nodeId: string | null;
  }> => {
    if (!selectedNode) return [];

    const items: Array<{ label: string; nodeId: string | null }> = [];
    const { categoryPath, type, label } = selectedNode;

    // Build ancestor breadcrumb items from categoryPath
    for (let i = 0; i < categoryPath.length; i++) {
      const segment = categoryPath[i];
      const ancestorPath = categoryPath.slice(0, i + 1).join('/');
      const ancestorNodeId = `category:${ancestorPath}`;

      // Resolve display label from the appropriate display map
      let displayLabel: string;
      if (i === 0) {
        displayLabel =
          ASSET_CLASS_TYPE_DISPLAY_MAP[
            segment as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP
          ] ?? segment;
      } else if (i === 1) {
        displayLabel =
          MARKET_SEGMENT_DISPLAY_MAP[
            segment as keyof typeof MARKET_SEGMENT_DISPLAY_MAP
          ] ?? segment;
      } else {
        displayLabel =
          SUB_ASSET_CLASS_DISPLAY_MAP[
            segment as keyof typeof SUB_ASSET_CLASS_DISPLAY_MAP
          ] ?? segment;
      }

      items.push({ label: displayLabel, nodeId: ancestorNodeId });
    }

    // For instrument nodes, add the instrument label as the last item
    if (type === 'instrument') {
      items.push({ label, nodeId: null }); // null = current, non-navigable
    }

    return items;
  }, [selectedNode]);

  // ---------------------------------------------------------------------------
  // Dirty Computation (render body — per CODING_STANDARDS)
  // ---------------------------------------------------------------------------

  const commissionJson = JSON.stringify(localCommission);
  const overnightJson = JSON.stringify(localOvernight);
  const conversionJson = JSON.stringify(localConversion);

  const isCommissionDirty = commissionJson !== baselineCommissionRef.current;
  const isOvernightDirty = overnightJson !== baselineOvernightRef.current;
  const isConversionDirty = conversionJson !== baselineConversionRef.current;

  const isDirtyForAnyTab = isCommissionDirty || isOvernightDirty || isConversionDirty;

  // Sync JSON snapshot refs (read by sync effect guard)
  localCommissionJsonRef.current = commissionJson;
  localOvernightJsonRef.current = overnightJson;
  localConversionJsonRef.current = conversionJson;

  // Sync dirty/discard/save refs for parent (BrokersTab) to read synchronously
  isDirtyRef.current = isCommissionDirty;

  discardRef.current = activeFeeConfig
    ? () => {
        setLocalCommission(activeFeeConfig.commission);
        setLocalOvernight(activeFeeConfig.overnightFinancing ?? null);
        setLocalConversion(activeFeeConfig.currencyConversion ?? null);
        if (hasQueuedFeeUpdate) {
          applyQueuedFeeUpdate();
        }
      }
    : null;

  saveRef.current = activeFeeConfig ? handleSave : null;

  // ---------------------------------------------------------------------------
  // Dirty→Clean Effect: Apply Queued Update
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isDirtyForAnyTab && hasQueuedFeeUpdate) {
      applyQueuedFeeUpdate();
    }
  }, [isDirtyForAnyTab, hasQueuedFeeUpdate, applyQueuedFeeUpdate]);

  // ---------------------------------------------------------------------------
  // Render Guards
  // ---------------------------------------------------------------------------

  // Node not found — BrokersTab owns selection reconciliation.
  // Render a stable empty state to avoid pane flash during transient frames.
  if (!selectedNode) {
    return (
      <div className="h-full flex items-center justify-center text-text-tertiary text-sm">
        Select a fee node to configure
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Computed UI State
  // ---------------------------------------------------------------------------

  const showFeeConflictBanner = hasQueuedFeeUpdate && isDirtyForAnyTab && !dismissedFeeConflict;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Card
        shadow="none"
        radius="none"
        className="h-full flex flex-col min-h-0 overflow-hidden"
      >
        {/* Header: Breadcrumb + Inherit from Parent + Notes */}
        <CardHeader className="shrink-0 border-b border-default-200 px-3 py-2 min-h-12.5 flex items-center justify-between gap-2">
          <Breadcrumbs variant="light" size="sm" className="min-w-0">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              const pressProps =
                !isLast && item.nodeId
                  ? { onPress: () => onNodeSelect(item.nodeId) }
                  : {};
              return (
                <BreadcrumbItem
                  key={item.nodeId ?? `current-${index}`}
                  isCurrent={isLast}
                  {...pressProps}
                  className={
                    isLast
                      ? 'text-text-primary font-medium'
                      : 'text-text-secondary hover:text-text-primary'
                  }
                >
                  {item.label}
                </BreadcrumbItem>
              );
            })}
          </Breadcrumbs>

          {activeFeeConfig ? (
            <div className="flex items-center gap-1 shrink-0">
              {/* Inherit from Parent — edit mode only, when ancestor exists */}
              {mode === 'edit' && nearestAncestorConfig && (
                <Button
                  variant="light"
                  size="sm"
                  onPress={() => setIsInheritConfirmOpen(true)}
                >
                  Inherit from Parent
                </Button>
              )}

              {/* Notes popover */}
              <Popover
                isOpen={isNotesOpen}
                onOpenChange={setIsNotesOpen}
                placement="bottom-end"
              >
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    aria-label="Fee configuration notes"
                    startContent={<NotepadText size={16} />}
                  >
                    Notes
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-72 flex flex-col gap-2">
                    <p className="text-xs font-medium text-text-secondary">
                      Notes
                    </p>
                    <TextArea
                      value={notesText}
                      onValueChange={setNotesText}
                      placeholder="Add notes about this fee configuration..."
                      minRows={3}
                      maxRows={8}
                      size="sm"
                    />
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        onPress={handleSaveNotes}
                        isLoading={isSavingNotes}
                        isDisabled={
                          notesText === (activeFeeConfig.notes ?? '')
                        }
                        startContent={
                          !isSavingNotes ? <Save size={14} /> : undefined
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            /* Invisible spacer — reserves action slot width so breadcrumb layout stays stable */
            <div className="shrink-0 min-w-16" aria-hidden="true" />
          )}
        </CardHeader>

        {/* Body: Commission Section + Content */}
        <CardBody className="flex-1 min-h-0 flex flex-col px-0 py-0 overflow-hidden">
          <div className="shrink-0 px-3 py-2 border-b border-default-200 flex items-center">
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-text-primary">Commission</span>
            </div>
            <Tooltip content="Configured in Broker Details above" placement="bottom" delay={400} size="sm">
              <div className="shrink-0 flex items-center gap-1 text-xs pl-2" tabIndex={0} aria-label="Account currency info">
                <Globe size={14} className="text-text-secondary" />
                <span className="text-text-secondary">Account Currency:</span>
                <span className={accountCurrency ? 'text-primary' : 'text-warning-500'}>
                  {accountCurrency ?? 'None configured'}
                </span>
              </div>
            </Tooltip>
          </div>
          <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-default-50 border-b border-default-100">
            <Info size={12} className="text-text-tertiary shrink-0" />
            <span className="text-xs text-text-tertiary">Financing &amp; FX configured at broker level</span>
          </div>

          {/* Cross-tab conflict banner */}
          {showFeeConflictBanner && (
            <div className="shrink-0 mx-3 my-2 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2">
              <AlertTriangle size={14} className="text-warning-600 shrink-0 mt-0.5" />
              <p className="text-sm text-warning-700 flex-1">
                External changes received. Save to keep your edits, or discard to load the latest version.
              </p>
              <Button
                variant="light"
                size="sm"
                className="shrink-0"
                onPress={() => setDismissedFeeConflict(true)}
              >
                Dismiss
              </Button>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto">
            {mode === 'inherited' ? (
              /* Inherited mode content */
              <div className="flex flex-col h-full">
                {/* Inherited banner */}
                {nearestAncestorConfig && nearestAncestorNodeId && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-default-100 border-b border-default-200">
                    <Info size={14} className="text-text-secondary shrink-0" />
                    <p className="text-sm text-text-secondary">
                      Inherited from{' '}
                      <Button
                        variant="light"
                        size="sm"
                        className="font-semibold h-auto p-0 min-w-0"
                        onPress={() => onNodeSelect(nearestAncestorNodeId)}
                      >
                        {nearestAncestorLabel}
                      </Button>
                    </p>
                  </div>
                )}

                {/* Tab content area */}
                <div className="flex-1 flex flex-col">
                  {nearestAncestorConfig ? (
                    /* Has inherited config — read-only display */
                    <>
                      <CommissionTabContent
                        commission={nearestAncestorConfig.commission}
                        mode="inherited"
                        accountCurrency={accountCurrency}
                      />
                      {/* Customize button or guidance */}
                      <div className="flex flex-col items-center gap-2 px-3 py-3 border-t border-default-100">
                        {feeConfigPath ? (
                          <Button
                            color="primary"
                            variant="flat"
                            size="sm"
                            onPress={handleCustomize}
                            isLoading={isCustomizing}
                          >
                            Customize Fees
                          </Button>
                        ) : (
                          <p className="text-xs text-text-tertiary">
                            Fee configurations can be added at market segment level or below
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    /* No config at any level */
                    <div className="flex-1 flex flex-col items-center justify-center px-3 py-3 gap-3">
                      <p className="text-sm text-text-tertiary text-center">
                        No fee configuration exists at this level or any ancestor
                      </p>
                      {feeConfigPath ? (
                        <Button
                          color="primary"
                          variant="flat"
                          size="sm"
                          onPress={handleCustomize}
                          isLoading={isCustomizing}
                        >
                          Customize Fees
                        </Button>
                      ) : (
                        <p className="text-xs text-text-tertiary">
                          Fee configurations can be added at market segment level or below
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Edit mode content */
              localCommission ? (
                <CommissionTabContent
                  commission={localCommission}
                  mode="edit"
                  accountCurrency={accountCurrency}
                  onChange={handleCommissionChange}
                />
              ) : null
            )}
          </div>
        </CardBody>

        {/* Footer: Save/Discard — edit mode only */}
        {activeFeeConfig && (
          <CardFooter className={`shrink-0 flex items-center justify-between px-3 py-2 ${isDirtyForAnyTab ? 'border-t-2 border-warning-300 bg-warning-50' : 'border-t border-default-200'}`}>
            <div>
              {isDirtyForAnyTab && (
                <span className="text-sm text-warning-600">
                  Unsaved changes
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="flat"
                size="sm"
                isDisabled={!isCommissionDirty || isSaving}
                onPress={handleDiscardCurrentTab}
              >
                Discard
              </Button>
              <Button
                color="primary"
                size="sm"
                isDisabled={!isDirtyForAnyTab || isSaving}
                isLoading={isSaving}
                onPress={handleSave}
                startContent={!isSaving ? <Save size={14} /> : undefined}
              >
                Save
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Inherit from Parent Confirmation Modal */}
      <Modal
        isOpen={isInheritConfirmOpen}
        onOpenChange={(open) => !open && setIsInheritConfirmOpen(false)}
        isDismissable={!isInheriting}
        hideCloseButton={isInheriting}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>Inherit from Parent</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              Remove custom fees for &quot;{selectedNode.label}&quot; and inherit from
              &quot;{nearestAncestorLabel}&quot;?
            </p>
            <p className="text-sm text-text-tertiary">
              The custom configuration will be archived and can be restored later.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsInheritConfirmOpen(false)} isDisabled={isInheriting}>
              Cancel
            </Button>
            <Button color="warning" onPress={handleInheritConfirm} isLoading={isInheriting}>
              Inherit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </>
  );
};

export default FeeConfigurationEditor;
